import {
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

export type LanguageType = { code: string; name: string; countryCode: string };

const LANG_STORAGE_KEY = 'lang-code';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));

  readonly availableLanguages: LanguageType[] = [
    { code: 'en-EN', name: 'english', countryCode: 'gb' },
    { code: 'fr-FR', name: 'français', countryCode: 'fr' },
    { code: 'de-DE', name: 'deutsch', countryCode: 'de' },
  ];

  readonly navigatorLanguage: string = this.isBrowser ? navigator.language.split('-')[0] : '';

  private readonly _currentLanguage: WritableSignal<LanguageType> = signal(
    this.resolveInitialLanguage(),
  );
  readonly currentLanguage: Signal<LanguageType> = this._currentLanguage.asReadonly();

  /** Applique la langue initiale, appelé au démarrage de l'appli. */
  init(): void {
    this.translateService.use(this._currentLanguage().code);
  }

  changeLanguage(code: string): void {
    const lang = this.availableLanguages.find((l) => l.code === code);
    if (!lang) return;
    this._currentLanguage.set(lang);
    this.translateService.use(lang.code);
    if (this.isBrowser) localStorage.setItem(LANG_STORAGE_KEY, lang.code);
  }

  // Priorité : localStorage => navigateur => default (anglais).
  private resolveInitialLanguage(): LanguageType {
    if (!this.isBrowser) return this.availableLanguages[0];
    const storedCode = localStorage.getItem(LANG_STORAGE_KEY);
    return (
      this.availableLanguages.find((l) => l.code === storedCode) ??
      this.availableLanguages.find((l) => l.code.split('-')[0] === this.navigatorLanguage) ??
      this.availableLanguages[0]
    );
  }
}
